// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCsvExport } from '@hooks/other/useCsvExport';
import { useExecutedTransfers } from '@hooks/queries/registry/transferInstruction/useExecutedTransfers';
import { useRejectedTransfers } from '@hooks/queries/registry/transferInstruction/useRejectedTransfers';
import { useTransferOffers } from '@hooks/queries/registry/transferInstruction/useTransferOffers';
import { useWithdrawnTransfers } from '@hooks/queries/registry/transferInstruction/useWithdrawnTransfers';
import {
  useGetAllInstrumentConfigurations,
  useInstrumentConfigurationsById,
} from '@hooks/queries/useGetAllInstrumentConfigurations';
import React, { useMemo, useState } from 'react';
import { GridRowParams } from '@mui/x-data-grid';
import { Batch } from '@daml.js/dtc-registry/lib/Dtc/Registry/V3/Types';
import { ChoiceDialog } from '../../../components/ChoiceDialog';
import Loading from '../../../components/Loading';
import { DataTable } from '../../../components/Table/DataTable';
import useRegistrarServices from '../../../hooks/queries/registry/onboarding/useRegistrarServices';
import useHoldings from '../../../hooks/queries/registry/useHoldings';
import { filterInstConfigsByInstrument } from '../../../utils/filterInstConfigsByInstrument';
import { PageWrapper } from '../../PageWrapper';
import { OfferTransferDialog } from './OfferTransferDialog';
import { TransferDetails } from './TransferDetails';
import { createHeaders } from './createHeaders';
import { Row, TransferExtended } from './types';
import { useCreateTransferActions } from './useCreateTransferActions';

interface TransfersProps {
  disableVirtualization?: boolean;
}

export const Transfers: React.FC<TransfersProps> = ({ disableVirtualization }) => {
  const registrarServices = useRegistrarServices();
  const holdings = useHoldings();
  const instConfigs = useGetAllInstrumentConfigurations();
  const transferOffers = useTransferOffers();
  const executedTransfers = useExecutedTransfers();
  const rejectedTransfers = useRejectedTransfers();
  const withdrawnTransfers = useWithdrawnTransfers();
  const [dialogName, setDialogName] = React.useState('');
  const onCloseDialog = () => {
    setDialogName('');
  };
  const [selectedRow, setSelectedRow] = useState<Row | undefined>(undefined);
  const onRowClick = (r: GridRowParams<Row>) => {
    setSelectedRow(r.row);
  };
  const onClose = () => {
    setSelectedRow(undefined);
  };
  const actions = useCreateTransferActions(onClose, selectedRow);

  const isLoading =
    registrarServices.isLoading ||
    holdings.isLoading ||
    !holdings.data ||
    transferOffers.isLoading ||
    !transferOffers.data ||
    executedTransfers.isLoading ||
    rejectedTransfers.isLoading ||
    withdrawnTransfers.isLoading;

  const rows = useMemo(() => {
    const result: Row[] = [];

    if (transferOffers.data) {
      for (const r of transferOffers.data) {
        const { transfer } = r.payload;
        const config = filterInstConfigsByInstrument(
          transfer.instrumentId.id,
          transfer.instrumentId.admin,
          instConfigs.data,
        );
        const operator = config?.payload.operator || '';
        const payload: TransferExtended = {
          sender: transfer.sender,
          receiver: transfer.receiver,
          instrumentIdentifier: {
            source: transfer.instrumentId.admin,
            id: transfer.instrumentId.id,
            scheme: 'RegistrarInternalScheme',
          },
          executeBefore: transfer.executeBefore,
          batch: {} as Batch,
          amount: transfer.amount,
          registrar: transfer.instrumentId.admin,
          reference: transfer.meta.values['splice.lfdecentralizedtrust.org/reason'],
          operator,
        };
        result.push({
          contractId: r.contractId,
          state: 'Offered2step',
          payload,
          createdAt: r.createdAt,
        });
      }
    }

    if (executedTransfers.data) {
      for (const r of executedTransfers.data) {
        const { transfer } = r.payload;
        const payload: TransferExtended = {
          ...transfer,
          batch: transfer.batch ?? ({} as Batch),
        };
        result.push({
          contractId: r.contractId,
          state: 'Executed2Step',
          payload,
          createdAt: r.createdAt,
        });
      }
    }

    if (rejectedTransfers.data) {
      for (const r of rejectedTransfers.data) {
        const { transfer } = r.payload;
        const payload: TransferExtended = {
          ...transfer,
          batch: transfer.batch ?? ({} as Batch),
        };
        result.push({
          contractId: r.contractId,
          state: 'Rejected2Step',
          payload,
          reason: r.payload.reason,
          createdAt: r.createdAt,
        });
      }
    }

    if (withdrawnTransfers.data) {
      for (const r of withdrawnTransfers.data) {
        const { transfer } = r.payload;
        const payload: TransferExtended = {
          ...transfer,
          batch: transfer.batch ?? ({} as Batch),
        };
        result.push({
          contractId: r.contractId,
          state: 'Withdrawn2Step',
          payload,
          createdAt: r.createdAt,
        });
      }
    }

    return result;
  }, [
    transferOffers.data,
    executedTransfers.data,
    rejectedTransfers.data,
    withdrawnTransfers.data,
    instConfigs.data,
  ]);

  const instrumentsById = useInstrumentConfigurationsById();

  const columns = useMemo(() => createHeaders(instrumentsById), [instrumentsById]);
  const { handleExport, isExportDisabled } = useCsvExport(columns, rows, {
    filename: 'transfers',
  });
  const buttons = [
    {
      name: 'Export CSV',
      onClick: handleExport,
      disabled: isExportDisabled,
    },
    {
      name: 'offer transfer',
      onClick: () => setDialogName('transfer'),
    },
  ];

  if (isLoading) return <Loading />;

  return (
    <PageWrapper buttons={buttons} pageName="Transfers">
      <ChoiceDialog
        open={!!selectedRow}
        onClose={onClose}
        actions={actions}
        title="Transfer Details"
      >
        {selectedRow && <TransferDetails row={selectedRow} />}
      </ChoiceDialog>
      {dialogName === 'transfer' && <OfferTransferDialog open onClose={onCloseDialog} />}
      <DataTable
        columns={columns}
        rows={rows}
        onRowClick={onRowClick}
        rowKey={(a) => `${a.state}:${a.contractId}`}
        disableVirtualization={disableVirtualization}
      />
    </PageWrapper>
  );
};
